/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { useEffect, useRef, useState } from "react";
import BaseButton from "components/common/Button/Base";
import BaseInput from "components/common/Input/Base";
import BaseCheck from "components/common/Check/Base";
import { PALETTE } from "styles/base/variables";
import { MemoRepeat, PayrollMemo } from "models/payrollManagement.model";

interface MemoPopoverProps {
  businessName: string;
  memos: PayrollMemo[];
  // 거래처 자유 메모 (Organization.memoMap). 한 줄짜리 텍스트, 없으면 null
  orgMemo: string | null;
  anchorRect: DOMRect | null;
  onClose: () => void;
  // 항목 단위 저장 — 각 호출은 서버 갱신 후 resolve 된다.
  onAdd: (text: string, repeat: MemoRepeat) => Promise<void>;
  onToggle: (memoId: string, done: boolean) => Promise<void>;
  onDelete: (memoId: string) => Promise<void>;
  // 거래처 메모 저장 (빈 문자열이면 삭제)
  onOrgMemoSave: (text: string) => Promise<void>;
}

function MemoPopover({
  businessName,
  memos,
  orgMemo,
  anchorRect,
  onClose,
  onAdd,
  onToggle,
  onDelete,
  onOrgMemoSave
}: MemoPopoverProps) {
  const [text, setText] = useState("");
  const [repeat, setRepeat] = useState<MemoRepeat>("NONE");
  const [memoDraft, setMemoDraft] = useState(orgMemo ?? "");
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMemoDraft(orgMemo ?? "");
  }, [orgMemo]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [onClose]);

  const run = (p: Promise<void>) => {
    setBusy(true);
    p.finally(() => setBusy(false));
    return p;
  };

  const add = () => {
    const v = text.trim();
    if (!v || busy) return;
    run(onAdd(v, repeat)).then(() => {
      setText("");
      setRepeat("NONE");
    });
  };

  const memoDirty = memoDraft.trim() !== (orgMemo ?? "").trim();
  const saveMemo = () => {
    if (busy || !memoDirty) return;
    run(onOrgMemoSave(memoDraft.trim()));
  };

  const top = anchorRect
    ? Math.min(anchorRect.bottom + 6, window.innerHeight - 260)
    : 120;
  const left = anchorRect
    ? Math.min(anchorRect.left, window.innerWidth - 320)
    : 120;

  return (
    <div
      ref={ref}
      css={css`
        position: fixed;
        z-index: 1300;
        top: ${top}px;
        left: ${left}px;
        width: 300px;
        background: ${PALETTE.light};
        border: 1px solid ${PALETTE.border};
        border-radius: 10px;
        box-shadow: 0 10px 28px rgba(0, 0, 0, 0.16);
        padding: 12px;
        ${busy && "opacity: 0.6; pointer-events: none;"}
      `}
    >
      <h5
        css={css`
          margin: 0 0 6px;
          font-size: 12px;
          color: ${PALETTE.textMid};
          font-weight: 700;
        `}
      >
        {businessName} — 이 거래처만의 할 일
      </h5>
      <ul
        css={css`
          list-style: none;
          margin: 0 0 8px;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        `}
      >
        {memos.length ? (
          memos.map((m) => (
            <li
              key={m.id}
              css={css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 6px;
                font-size: 12px;
                padding: 4px 2px;
                border-bottom: 1px solid ${PALETTE.borderLight};
              `}
            >
              <label
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 7px;
                  cursor: pointer;
                  flex: 1;
                `}
              >
                <input
                  type="checkbox"
                  checked={m.done}
                  onChange={() => run(onToggle(m.id, !m.done))}
                />
                <span
                  css={css`
                    ${m.done &&
                    `color:${PALETTE.textMid};text-decoration:line-through;`}
                  `}
                >
                  {m.text}
                </span>
              </label>
              <span
                title={
                  m.repeat === "MONTHLY"
                    ? "월 반복 — 지급월마다 따로 체크"
                    : m.repeat === "YEARLY"
                    ? "연 반복"
                    : "일회성 — 한 번 완료하면 유지"
                }
                css={css`
                  font-size: 9.5px;
                  font-weight: 700;
                  padding: 1px 5px;
                  border-radius: 4px;
                  white-space: nowrap;
                  background: ${m.repeat !== "NONE"
                    ? `${PALETTE.primary}22`
                    : PALETTE.borderLight};
                  color: ${m.repeat !== "NONE"
                    ? PALETTE.primary
                    : PALETTE.textMid};
                `}
              >
                {m.repeat === "MONTHLY"
                  ? "↻ 월"
                  : m.repeat === "YEARLY"
                  ? "↻ 연"
                  : "1회"}
              </span>
              <button
                type="button"
                onClick={() => run(onDelete(m.id))}
                title="삭제"
                css={css`
                  border: 0;
                  background: transparent;
                  color: ${PALETTE.textMid};
                  cursor: pointer;
                  font-size: 11px;
                  padding: 2px 4px;
                  &:hover {
                    color: ${PALETTE.error};
                  }
                `}
              >
                ✕
              </button>
            </li>
          ))
        ) : (
          <li
            css={css`
              color: ${PALETTE.textMid};
              font-size: 11px;
              padding: 4px 2px;
            `}
          >
            등록된 할 일이 없습니다.
          </li>
        )}
      </ul>
      <div
        css={css`
          display: flex;
          gap: 6px;
        `}
      >
        <BaseInput
          placeholder="할 일 추가 (예: 주민세(종업원분) 신고)"
          value={text}
          onInput={(e) => setText(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
          }}
          css={css`
            flex: 1;
            font-size: 12px;
          `}
        />
        <BaseButton
          variant="contained"
          color={PALETTE.primary}
          onClick={add}
          css={css`
            padding: 6px 12px;
            font-size: 12px;
            white-space: nowrap;
          `}
        >
          추가
        </BaseButton>
      </div>
      <div
        css={css`
          margin-top: 8px;
          font-size: 11px;
          color: ${PALETTE.textMid};
        `}
      >
        <BaseCheck
          id="memoRepeat"
          checked={repeat === "MONTHLY"}
          onChange={(e) => setRepeat(e.target.checked ? "MONTHLY" : "NONE")}
          label=" ↻ 월 반복 업무 (지급월마다 완료 여부 따로 관리)"
        />
      </div>

      <div
        css={css`
          margin-top: 12px;
          padding-top: 10px;
          border-top: 1px solid ${PALETTE.borderLight};
        `}
      >
        <h5
          css={css`
            margin: 0 0 6px;
            font-size: 12px;
            color: ${PALETTE.textMid};
            font-weight: 700;
          `}
        >
          거래처 메모
        </h5>
        <div
          css={css`
            display: flex;
            gap: 6px;
          `}
        >
          <BaseInput
            placeholder="거래처 메모 (예: 대표자 직접 연락 요망)"
            value={memoDraft}
            onInput={(e) => setMemoDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveMemo();
            }}
            css={css`
              flex: 1;
              font-size: 12px;
            `}
          />
          <BaseButton
            variant="contained"
            color={PALETTE.progressCompleted}
            onClick={saveMemo}
            disabled={!memoDirty}
            css={css`
              padding: 6px 12px;
              font-size: 12px;
              white-space: nowrap;
            `}
          >
            {memoDraft.trim() ? "저장" : "삭제"}
          </BaseButton>
        </div>
      </div>
    </div>
  );
}

export default MemoPopover;
